import React, { useEffect, useRef, useState } from 'react';
import { useReducedMotion } from 'framer-motion';
import { COUNT_UP_DURATION, easeOutCubic, formatNumber } from '../utils/animations';

// Animates a number counting up from 0 to `value` on mount (ease-out),
// formatting with commas/decimals/prefix/suffix as it lands. Jumps straight
// to the final value when the user prefers reduced motion.
const CountUpNumber = React.memo(({
    value,
    duration = COUNT_UP_DURATION,
    decimals = 0,
    prefix = '',
    suffix = '',
    withCommas = true,
    delay = 0,
    as: Tag = 'span',
    ...rest
}) => {
    const reduceMotion = useReducedMotion();
    const target = Number(value) || 0;
    const [display, setDisplay] = useState(reduceMotion ? target : 0);
    const frameRef = useRef(null);

    useEffect(() => {
        if (reduceMotion) {
            setDisplay(target);
            return undefined;
        }

        let cancelled = false;
        let startTime = null;

        const tick = (timestamp) => {
            if (cancelled) return;
            if (startTime === null) startTime = timestamp;
            const progress = Math.min((timestamp - startTime) / duration, 1);
            setDisplay(target * easeOutCubic(progress));
            if (progress < 1) {
                frameRef.current = requestAnimationFrame(tick);
            }
        };

        const delayTimer = setTimeout(() => {
            frameRef.current = requestAnimationFrame(tick);
        }, delay * 1000);

        return () => {
            cancelled = true;
            clearTimeout(delayTimer);
            if (frameRef.current) cancelAnimationFrame(frameRef.current);
        };
    }, [target, duration, delay, reduceMotion]);

    return (
        <Tag {...rest}>
            {prefix}{formatNumber(display, decimals, withCommas)}{suffix}
        </Tag>
    );
});

CountUpNumber.displayName = 'CountUpNumber';

export default CountUpNumber;
